import React, { FC, useState, useEffect, useContext } from 'react';

import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';

import ErrorMessage from '../../../sharedModules/layout/ErrorMessage';
import LoadingWrapper from '../../../sharedModules/layout/LoadingWrapper';

import { UsersContext } from '../contexts/UsersContext';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';

import axios from '../../../sharedModules/utils/axios.utils';

interface UserDetailsCardProps {
  userId?: any;
}

const UserDetailsCard: FC<UserDetailsCardProps> = (props) => {
  let { userId } = props;

  const { updateUser, user, userLoading, fetchUser } = useContext(UsersContext);
  const { setSnackBarOpen, setSnackBarMessage } = useContext(SnackBarContext);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [signatureLoading, setSignatureLoading] = useState<boolean>(false);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreviewUrl, setSignaturePreviewUrl] = useState<string | null>(null);

  const onSuccess = () => {
    setSnackBarOpen(true);
    setSnackBarMessage('User Details Updated Successfully');
    setSignatureFile(null);
    if (signaturePreviewUrl) {
      URL.revokeObjectURL(signaturePreviewUrl);
      setSignaturePreviewUrl(null);
    }
    if (fetchUser) {
      fetchUser(userId);
    }
  };

  const handleSignatureChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      setSignatureFile(file);

      // Clean up previous preview URL to prevent memory leaks
      if (signaturePreviewUrl) {
        URL.revokeObjectURL(signaturePreviewUrl);
      }

      setSignaturePreviewUrl(URL.createObjectURL(file));

      // Automatically update user with the signature file
      const formData = new FormData();
      formData.append('user[signature]', file);
      updateUser(userId || '', formData, onSuccess);
    }
  };

  // Cleanup preview URL on component unmount
  useEffect(() => {
    return () => {
      if (signaturePreviewUrl) {
        URL.revokeObjectURL(signaturePreviewUrl);
      }
    };
  }, [signaturePreviewUrl]);

  const handleGenerateSignatureImage = async () => {
    setSignatureLoading(true);
    try {
      await axios.put(`/admin/generate_user_signature/${userId}`);
      fetchUser(userId);

      setErrorMessage('');
    } catch (error) {
      setErrorMessage('Something went wrong');
    } finally {
      setSignatureLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={userLoading || signatureLoading}>
      <Accordion>
        <AccordionSummary>
          <Typography variant="h5" component="h5">
            Signature
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ErrorMessage message={errorMessage} />
            </Grid>
            <Grid item xs={12}>
              {user?.signature_url && (
                <div style={{ marginBottom: '10px' }}>
                  <img
                    src={user.signature_url}
                    alt="User signature"
                    style={{ maxHeight: '100px', border: '1px solid #ccc' }}
                  />
                </div>
              )}
              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="signature-upload"
                type="file"
                onChange={handleSignatureChange}
              />
              <label htmlFor="signature-upload">
                <Button variant="contained" component="span">
                  Upload Signature File
                </Button>
              </label>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" color="primary" onClick={handleGenerateSignatureImage}>
                Generate Signature Image
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </LoadingWrapper>
  );
};

export default UserDetailsCard;
